import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { RegExp } from 'src/app/core/config/regex';
import { Employee, Member, Team, TeamRole } from 'src/app/core/models/project-pilot.model';
import { Context, Role } from 'src/app/shared/models/context';
import { EmployeeData, Message, PrivilegeType, TableData } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { CompanyService } from 'src/app/shared/services/company.service';
import { DataService } from 'src/app/shared/services/data.service';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-manage-team',
  templateUrl: './manage-team.component.html',
  styleUrls: ['./manage-team.component.scss']
})
export class ManageTeamComponent implements OnInit {

  @Input() team: Team;
  @Input() context: Context = {} as Context;
  @Input() teams: Team[] = [];
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  @ViewChild("ngTeamSelect") ngTeamSelect: NgSelectComponent;
  @ViewChild('roleSelect') roleSelect!: NgSelectComponent;
  public tableData: TableData = { content: [] } as TableData;
  public isLoading: boolean = true;
  public members: any[] = [];
  public selectedMemberIds: number[] = [];
  public selectedRoleIds: number[] = [];
  public teamForm: FormGroup;
  public submitted: boolean = false;
  public isMemberSelected: boolean = false;
  public privilege: PrivilegeType;
  public isNewUser: boolean = false;
  private modified: boolean = false;
  private roles: any[] = [];
  public pageSizes: number[] = [];
  private employees: Employee[] = [];
  public editingIndex: number = null;
  public selectedRole: number = null;
  public teamId: number;
  private pageDataKey: string = 't-r';
  public searchKeyword: string = "";

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private commonService: CommonService,
    private projectPilotService: ProjectPilotService,
    private dataService: DataService,
    private projectPilotSevice: ProjectPilotService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.tableData = this.commonService.getDefaultPageData(this.pageDataKey);
    this.dataService.privilege().subscribe(privilege => { this.privilege = this.commonService.getPrivileges('DELETE') });
    this.setTeam(this.team);
    if (this.team?.id) {
      this.getMembers();
    }
    this.projectPilotSevice.getTeamRoles(this.tableData.number - 1, this.tableData.size, false).subscribe((roles: any) => this.roles = roles.content);
    this.projectPilotService.getEmployees().subscribe((employees: Employee[]) => this.employees = employees);
  }

  public setTeam(team?: Team) {
    if (!team)
      team = {
        id: null,
        name: "",
        code: "",
        description: "",
        disabled: false,
      } as Team;
    this.teamForm = this.fb.group({
      id: this.fb.control(team?.id || null),
      name: this.fb.control(team.name, [Validators.required, Validators.pattern(RegExp.name)]),
      code: this.fb.control(team.code || null),
      description: this.fb.control(team.description, [Validators.pattern(RegExp.description)]),
      disabled: this.fb.control(team.disabled, []),
    });
    this.teamForm.valueChanges.subscribe(value => this.modified = true);
  }

  get t() {
    return this.teamForm.controls;
  }

  public saveTeam(inProgress?: any) {
    this.submitted = true;
    if (this.teamForm.valid) {
      const team: Team = this.teamForm.value;
      if (this.teams.some(w =>
        w.name.toLowerCase() === team.name.toLowerCase() && w.id !== team.id)) {
        this.commonService.setToastr(400, `Name already exists.`);
        return;
      }
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Team ${team.name} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (team.id) {
        this.projectPilotService.updateTeam(team.id, team).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.passEntry.emit(response);
              this.commonService.setToastr(200, response.message);
            } else {
              this.commonService.setToastr(500, response.message);
            }
            inProgress.value = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.projectPilotService.createTeam(team).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.teamId = parseInt(response.payload)
              this.teamForm.get('id')?.setValue(this.teamId);
              this.getTeam();
              this.getMembers();
              this.passEntry.emit(response);
              this.commonService.setToastr(200, response.message);
            } else {
              this.commonService.setToastr(500, response.message);
            }
            inProgress.value = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }

  public getTeam() {
    this.projectPilotService.getTeam(this.teamId).subscribe({
      next: (response: Team) => {
        this.setTeam(response);
      }
    })
  }

  public addMembers(isSaving?: any) {
    if (!this.isNewUser) {
      this.isNewUser = true;
      setTimeout(() => {
        this.ngTeamSelect.open();
        this.ngTeamSelect.focus();
      }, 0);
    } else {
      if (this.selectedMemberIds.length > 0) {
        isSaving.value = true;
        this.projectPilotService.addMembers(this.t.id.value, this.selectedMemberIds).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.isNewUser = false;
              this.commonService.setToastr(200, response.message);
              this.passEntry.emit(response);
              this.getMembers();
              this.selectedMemberIds = [];
            } else {
              this.commonService.setToastr(500, response.message);
            }
            isSaving.value = false;
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(500, error?.error?.name || error.error);
            isSaving.value = false;
          },
        });
      } else {
        this.isMemberSelected = true;
      }
    }
  }

  public cancel() {
    this.isNewUser = false;
    this.isMemberSelected = false;
    this.selectedMemberIds = [];
  }

  public getMembers(clear: boolean = true) {
    if (clear) {
      this.isLoading = true;
      this.teams = [];
    }
    this.projectPilotService.getMembers(this.t.id.value).subscribe({
      next: (member: Member[]) => {
        this.members = member;
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
        this.isLoading = false;
      }
    });
  }

  public deleteMember(member: Member, index: number) {
    member.isDeleteInProgress = true;
    this.projectPilotSevice.deleteMembers(member.id).subscribe({
      next: (message: Message) => {
        if (!message.failed) {
          this.commonService.setToastr(200, message.message);
          this.roles.splice(index, 1);
          this.getMembers();
          member.isDeleteInProgress = false;
        } else {
          this.commonService.setToastr(500, message.message);
          member.isDeleteInProgress = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
        member.isDeleteInProgress = false;
      },
    });
  }

  public updateRole(member: Member) {
    this.projectPilotService.updateMember(member.id, this.selectedRole).subscribe({
      next: (response: Message) => {
        if (!response.failed) {
          this.commonService.setToastr(200, `Role ${response.message}`);
          this.getMembers();
          this.passEntry.emit(response);
          this.editingIndex = null;
        } else {
          this.commonService.setToastr(500, response.message);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
      },
    });
  }

  public enableDropdown(index: number, roleId: number) {
    this.editingIndex = index;
    this.selectedRole = roleId;
    setTimeout(() => {
      if (this.roleSelect) {
        this.roleSelect.open();
      }
    }, 0);
  }

  public cancelEdit(index: number) {
    this.editingIndex = null;
  }

  public isEmployeeDisabled(employeeId: number): boolean {
    return this.members.some(member => member.employeeId === employeeId);
  }

  public toggleTeamMemberStatus(member: Member) {
    const newStatus = !member.disabled;
    this.projectPilotService.toggleTeamMemberStatus(member.id, newStatus).subscribe({
      next: (response: Message) => {
        if (!response.failed) {
          member.disabled = newStatus;
          this.commonService.setToastr(200, `Member ${member.employeeName} is now ${newStatus ? 'disabled' : 'enabled'}!`);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
      },
    });
  }
}
